/**
 * Active playable character — one player at a time, persisted locally.
 * Each backyard also remembers its own last Friends pick, falling back to
 * that yard's default character on first visit.
 *
 * Friends picked on the Places map are a travel preference: every backyard
 * is seeded with that character so the next location you enter uses them
 * (until you change Friends inside a specific yard).
 */

import {
  DEFAULT_CHARACTER_BY_BACKYARD,
  DEFAULT_PLAYABLE_CHARACTER_ID,
  getPlayableCharacter,
  isPlayableCharacterId,
  type PlayableCharacterDef,
  type PlayableCharacterId,
} from '@/world-prototype/player/playableCharacters';

const STORAGE_KEY = 'porchlight-active-player-character';
const YARD_STORAGE_KEY = 'porchlight-player-by-yard';
const MAP_TRAVEL_KEY = 'porchlight-map-travel-character';
/** Session-only yard picks — hard refresh restores each yard's default. */
const SESSION_YARD_KEYS: Partial<Record<BackyardId, string>> = {
  hubert: 'porchlight-hubert-yard-character',
  raleigh: 'porchlight-raleigh-yard-character',
};

type BackyardId = keyof typeof DEFAULT_CHARACTER_BY_BACKYARD;

const BACKYARD_IDS = Object.keys(
  DEFAULT_CHARACTER_BY_BACKYARD,
) as BackyardId[];

let memoryId: PlayableCharacterId | null = null;
/** Friends pick made on the map — applied when entering a backyard. */
let mapTravelCharacterId: PlayableCharacterId | null = null;
const listeners = new Set<(id: PlayableCharacterId) => void>();

function isBackyardId(id: string): id is BackyardId {
  return id in DEFAULT_CHARACTER_BY_BACKYARD;
}

function readStored(): PlayableCharacterId {
  if (typeof window === 'undefined') return DEFAULT_PLAYABLE_CHARACTER_ID;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && isPlayableCharacterId(raw)) return raw;
  } catch {
    /* private mode / blocked storage */
  }
  return DEFAULT_PLAYABLE_CHARACTER_ID;
}

function writeStored(id: PlayableCharacterId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}

function readMapTravelStored(): PlayableCharacterId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MAP_TRAVEL_KEY);
    if (raw && isPlayableCharacterId(raw)) return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function writeMapTravelStored(id: PlayableCharacterId | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id) window.sessionStorage.setItem(MAP_TRAVEL_KEY, id);
    else window.sessionStorage.removeItem(MAP_TRAVEL_KEY);
  } catch {
    /* ignore */
  }
}

function getMapTravelCharacterId(): PlayableCharacterId | null {
  if (mapTravelCharacterId) return mapTravelCharacterId;
  const stored = readMapTravelStored();
  if (stored) mapTravelCharacterId = stored;
  return mapTravelCharacterId;
}

function setMapTravelCharacterId(id: PlayableCharacterId | null): void {
  mapTravelCharacterId = id;
  writeMapTravelStored(id);
}

function readYardMap(): Partial<Record<BackyardId, PlayableCharacterId>> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(YARD_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out: Partial<Record<BackyardId, PlayableCharacterId>> = {};
    for (const [yard, value] of Object.entries(parsed)) {
      if (
        isBackyardId(yard) &&
        typeof value === 'string' &&
        isPlayableCharacterId(value)
      ) {
        out[yard] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function sessionYardKey(yard: BackyardId): string | null {
  return SESSION_YARD_KEYS[yard] ?? null;
}

function readSessionYardCharacter(yard: BackyardId): PlayableCharacterId | null {
  const key = sessionYardKey(yard);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw && isPlayableCharacterId(raw)) return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function writeSessionYardCharacter(
  yard: BackyardId,
  id: PlayableCharacterId | null,
): void {
  const key = sessionYardKey(yard);
  if (!key || typeof window === 'undefined') return;
  try {
    if (id) window.sessionStorage.setItem(key, id);
    else window.sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function rememberedYardCharacter(
  yard: BackyardId,
): PlayableCharacterId | undefined {
  if (sessionYardKey(yard)) {
    return readSessionYardCharacter(yard) ?? undefined;
  }
  return readYardMap()[yard];
}

function rememberYardCharacter(yard: BackyardId, id: PlayableCharacterId): void {
  if (sessionYardKey(yard)) {
    writeSessionYardCharacter(yard, id);
    return;
  }
  writeYardCharacter(yard, id);
}

function writeYardCharacter(yard: BackyardId, id: PlayableCharacterId): void {
  if (typeof window === 'undefined') return;
  try {
    const next = { ...readYardMap(), [yard]: id };
    window.localStorage.setItem(YARD_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function seedAllYards(id: PlayableCharacterId): void {
  for (const yard of BACKYARD_IDS) {
    rememberYardCharacter(yard, id);
  }
}

/** Current active player character id (defaults to Big Sister). */
export function getActivePlayerCharacterId(): PlayableCharacterId {
  if (memoryId == null) memoryId = readStored();
  return memoryId;
}

export function getActivePlayableCharacter(): PlayableCharacterDef {
  return getPlayableCharacter(getActivePlayerCharacterId());
}

/**
 * Set the active player. Persists locally and notifies listeners.
 * Invalid ids are ignored (keeps current).
 * When `yardId` is a known backyard, remembers the pick for that yard.
 * When `yardId` is omitted/null (Places map), stores a travel preference
 * and seeds every backyard so the next location uses that friend.
 */
export function setActivePlayerCharacterId(
  id: string,
  yardId?: string | null,
): boolean {
  if (!isPlayableCharacterId(id)) return false;
  if (yardId && isBackyardId(yardId)) {
    rememberYardCharacter(yardId, id);
    setMapTravelCharacterId(null);
  } else if (!yardId) {
    // Places map (or any non-yard context): travel pick for upcoming locations.
    setMapTravelCharacterId(id);
    seedAllYards(id);
  }
  if (memoryId === id) {
    writeStored(id);
    return true;
  }
  memoryId = id;
  writeStored(id);
  for (const listener of listeners) listener(id);
  return true;
}

/**
 * Apply the character for a backyard.
 * Priority: map Friends travel pick → last Friends pick in this yard → yard default.
 * Returns the character id now active.
 */
export function applyCharacterForBackyard(spaceId: string): PlayableCharacterId {
  if (!isBackyardId(spaceId)) {
    return getActivePlayerCharacterId();
  }
  const travel = getMapTravelCharacterId();
  if (travel) {
    // Keep travel active across locations until Friends is changed in a yard.
    rememberYardCharacter(spaceId, travel);
    if (memoryId !== travel) {
      memoryId = travel;
      writeStored(travel);
      for (const listener of listeners) listener(travel);
    } else {
      writeStored(travel);
    }
    return travel;
  }
  const remembered = rememberedYardCharacter(spaceId);
  const next = remembered ?? DEFAULT_CHARACTER_BY_BACKYARD[spaceId];
  if (memoryId !== next) {
    memoryId = next;
    writeStored(next);
    for (const listener of listeners) listener(next);
  } else {
    writeStored(next);
  }
  rememberYardCharacter(spaceId, next);
  return next;
}

export function defaultCharacterForBackyard(
  spaceId: string,
): PlayableCharacterId | null {
  if (!isBackyardId(spaceId)) return null;
  return DEFAULT_CHARACTER_BY_BACKYARD[spaceId];
}

export function subscribeActivePlayer(
  listener: (id: PlayableCharacterId) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test helper — clear in-memory cache (does not wipe localStorage). */
export function resetActivePlayerMemoryForTests(): void {
  memoryId = null;
  mapTravelCharacterId = null;
  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.removeItem(MAP_TRAVEL_KEY);
      for (const key of Object.values(SESSION_YARD_KEYS)) {
        if (key) window.sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }
}
